import { AddressError } from '@components/Address/AddressError';
import { TireHotelPickupAddressSuggestion } from '@components/BookingFlow/Common/TireHotelPickupAddressSuggestion';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  LicensePlate,
  SalesItemBookingCategoryType,
  SalesItemWheelsPickupOptionRecord,
  TireHotelLocationRecord
} from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { BOOKING_AVAILABLE_COUNTRY_CODES, SALES_ITEM_BOOKING_FLOW_TYPE } from 'noddi-types';
import { cn, noddiColors, NoddiIcon } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiButton, NoddiDummyButton, NoddiInfoBanner, themeColors } from 'noddi-ui-web';
import { compareLicensePlates, formatCurrencyAmount } from 'noddi-util';
import {
  AddressSearch,
  getHardCodedPickupText,
  getLongAddressName,
  getPickupAddressAwaitingTireHotelDecision,
  TireHotelLocationsSelect,
  useBookingActions,
  useBookingAddress,
  useBookingSelectedSalesItemsCars
} from 'noddi-web';
import { useState } from 'react';
import { useCurrentBrand } from '../../../../stores/BrandStore/brandStore';
import { getCommonTranslations } from '../../../../utils/commonTranslations';

type WheelStorageOptionProps = {
  tireHotelSalesItem: SalesItemWheelsPickupOptionRecord;
  licensePlate: LicensePlate;
};

export const TireHotelPickUpOption = ({ tireHotelSalesItem, licensePlate }: WheelStorageOptionProps) => {
  const { currentBrand } = useCurrentBrand();

  const {
    name,
    price,
    salesItemId,
    requiresPickupAddressSelection,
    mutuallyExclusiveSalesItemIds,
    isAddonsAvailable,
    isWheelPickupRequired,
    bookingCategoryType
  } = tireHotelSalesItem;
  const selectedSalesItemsCars = useBookingSelectedSalesItemsCars();
  const selectedAddress = useBookingAddress();

  const selectedSalesItemsCar = selectedSalesItemsCars.find(
    (item) => compareLicensePlates(item.licensePlate, licensePlate) && item.salesItemId === salesItemId
  );

  const isSelected = !!selectedSalesItemsCar;

  const pickUpAddress = selectedSalesItemsCar?.metaData?.selectedTireHotelPickUpAddress;
  const selectedTireHotelLocation = selectedSalesItemsCar?.metaData?.selectedTireHotelLocation;

  // Only need state for the UI mode toggle
  const [useCustomAddress, setUseCustomAddress] = useState(!!pickUpAddress && !selectedTireHotelLocation);
  const addressAwaitingDecision = getPickupAddressAwaitingTireHotelDecision(selectedSalesItemsCar);

  const { removeSalesItem, addSalesItem } = useBookingActions();

  const removeOption = () => {
    removeSalesItem({ licensePlate, salesItemId, type: SALES_ITEM_BOOKING_FLOW_TYPE.TIRE_HOTEL_PICKUP });
  };

  const pickUpAtHomeToggle = () => {
    isSelected
      ? removeOption()
      : addSalesItem({
          licensePlate,
          salesItemId,
          type: SALES_ITEM_BOOKING_FLOW_TYPE.TIRE_HOTEL_PICKUP,
          mutuallyExclusiveSalesItemIds,
          isAddonsAvailable,
          isWheelPickupRequired,
          bookingCategoryType: bookingCategoryType as SalesItemBookingCategoryType
        });
  };

  const {
    mutateAsync: createAddressFromPlaceId,
    reset: resetCreateAddressFromPlaceId,
    isPending,
    error: addressError,
    data: createdAddress
  } = noddiAsync.usePost({
    type: URLKeys.postAddressCreatedFromPlaceId,
    queryConfig: {
      onSuccess: ({ data: { isInDeliveryArea, ...rest } }) => {
        if (isInDeliveryArea) {
          addSalesItem({
            licensePlate,
            salesItemId,
            type: SALES_ITEM_BOOKING_FLOW_TYPE.TIRE_HOTEL_PICKUP,
            mutuallyExclusiveSalesItemIds,
            metaData: { selectedTireHotelPickUpAddress: rest },
            isAddonsAvailable,
            isWheelPickupRequired,
            bookingCategoryType: bookingCategoryType as SalesItemBookingCategoryType
          });
        } else {
          removeOption();
        }
      },
      onError: () => {
        removeOption();
      }
    }
  });

  const handleTireHotelLocationSelection = (location: TireHotelLocationRecord | undefined) => {
    if (location) {
      setUseCustomAddress(false);
      addSalesItem({
        licensePlate,
        salesItemId,
        type: SALES_ITEM_BOOKING_FLOW_TYPE.TIRE_HOTEL_PICKUP,
        mutuallyExclusiveSalesItemIds,
        metaData: {
          // todo ts-package error fix
          selectedTireHotelPickUpAddress: location.address,
          selectedTireHotelLocation: location
        },
        isAddonsAvailable,
        isWheelPickupRequired,
        bookingCategoryType: bookingCategoryType as SalesItemBookingCategoryType
      });
    } else {
      removeOption();
    }
  };

  const isFree = price.priceGross.amount === 0;
  const priceFormatted = isFree
    ? t`Free`
    : formatCurrencyAmount({
        money: price.priceGross,
        decimals: 0
      });

  if (!requiresPickupAddressSelection) {
    return (
      <NoddiBasicCard onClick={pickUpAtHomeToggle} selected={isSelected} className='flex flex-col gap-2'>
        <span className='text-lg font-medium'>{name}</span>
        <span>{getHardCodedPickupText(selectedSalesItemsCars, 'home')}</span>
        <div className='-ml-1 flex items-baseline gap-1'>
          <NoddiIcon name='MapPin' color={isSelected ? themeColors['card-selected-foreground'] : noddiColors.black} />
          {selectedAddress && <span>{getLongAddressName(selectedAddress)}</span>}
        </div>
        <div className='flex items-center justify-between'>
          <span className={cn('font-semibold', isFree && !isSelected && 'text-successDark')}>{priceFormatted}</span>
          <NoddiDummyButton variant='outline' startIcon={isSelected ? 'Minus' : 'Plus'} size='small'>
            {isSelected ? <Trans>Remove</Trans> : <Trans>Choose</Trans>}
          </NoddiDummyButton>
        </div>
      </NoddiBasicCard>
    );
  }

  const isSelectedAndHasAddress = isSelected && (pickUpAddress?.id || selectedTireHotelLocation?.address?.id);

  return (
    <NoddiBasicCard
      className={cn(
        'flex flex-col gap-2',
        isSelectedAndHasAddress && 'bg-card-selected-background text-card-selected-foreground'
      )}
    >
      <span className='text-lg font-medium'>{name}</span>
      <span>{getHardCodedPickupText(selectedSalesItemsCars, 'otherLocation')}</span>

      <span className={cn('font-semibold', isFree && !isSelected && 'text-successDark')}>{priceFormatted}</span>

      <div className={cn(isSelected && 'text-black')}>
        {!useCustomAddress ? (
          <TireHotelLocationsSelect
            selectedLocation={selectedTireHotelLocation}
            onLocationChange={handleTireHotelLocationSelection}
            placeholder={t`Enter your tire hotel`}
            validForAddressId={selectedAddress?.id}
          />
        ) : (
          <AddressSearch
            countryCodes={BOOKING_AVAILABLE_COUNTRY_CODES}
            defaultValue={pickUpAddress}
            translations={getCommonTranslations().addressSearch}
            // oxlint-disable-next-line typescript/no-misused-promises -- async onSelect runs createAddressFromPlaceId(); React discards the returned promise, so a rejection is unhandled unless the body catches
            onSelect={async (addressSuggestion) => {
              await createAddressFromPlaceId({ placeId: addressSuggestion.placeId, brandDomains: [currentBrand] });
            }}
            onClear={() => {
              resetCreateAddressFromPlaceId();
              removeOption();
            }}
            loading={isPending}
          />
        )}
      </div>

      <NoddiButton
        variant={isSelected ? 'lightLink' : 'link'}
        size='small'
        className='-ml-2'
        startIcon='Edit'
        onClick={() => {
          // Clear any address creation errors when switching modes
          resetCreateAddressFromPlaceId();
          setUseCustomAddress(!useCustomAddress);
          // Remove any existing selection since we're switching modes
          if (isSelected) {
            removeOption();
          }
        }}
      >
        {useCustomAddress ? t`Enter your tire hotel` : t`Enter address instead`}
      </NoddiButton>

      <AddressError addressError={addressError} />

      {addressAwaitingDecision && (
        <TireHotelPickupAddressSuggestion
          addressId={addressAwaitingDecision.id}
          onSelectLocation={handleTireHotelLocationSelection}
        />
      )}

      {createdAddress && !createdAddress.data.isInDeliveryArea && (
        <NoddiInfoBanner
          variant='error'
          className='mt-4 text-black'
          texts={[{ value: getCommonTranslations().weDoNotOfferTheseServicesAtThisAddress }]}
        />
      )}
    </NoddiBasicCard>
  );
};
